import { FC, useContext, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { getProjectStatusValues, ProjectStatus, UpdateProjectBody } from "../../http/types/projects";
import { useProjectsApi } from "../../http/useProjects";
import { getUILanguages, Language } from "../../types";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import { CustomSelect } from "../ui/select/CustomSelect";
import Textarea from "../ui/Textarea";

interface ProjectInfosDialogFormBody {
  name: string;
  language: Language | null;
  status: ProjectStatus | null;
  company?: string;
  contactPerson?: string;
  description?: string;
}

interface ProjectInfosDialogProps {
  projectId: string;
  isAdmin?: boolean;
  onClose: () => void;
}

const ProjectInfosDialog: FC<ProjectInfosDialogProps> = ({ projectId, isAdmin, onClose }: ProjectInfosDialogProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const projectsApi = useProjectsApi();
  const aborter = useRef(new AbortController());

  const { data: project, trigger, isMutating: isLoading } = projectsApi.getProject(aborter.current.signal);

  useEffect(() => {
    trigger(projectId);
  }, [projectId]);

  const languages = getUILanguages(i18n.translation);
  const stati = getProjectStatusValues(i18n.translation);
  const canEdit = !!project?.canEdit;

  const defaultValues: ProjectInfosDialogFormBody = {
    name: project?.name ?? "",
    language: languages.find((x) => x.value === project?.language) ?? null,
    status: stati.find((x) => x.value === project?.status) ?? null,
    company: project?.company,
    contactPerson: project?.contactPerson,
    description: project?.description,
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.translation.projects.infos}>
      <Form<ProjectInfosDialogFormBody>
        defaultValues={defaultValues}
        disabled={isLoading}
        onSubmit={
          canEdit
            ? () => async (formData) => {
                if (!project) {
                  return;
                }
                const body: UpdateProjectBody = {
                  name: formData.name,
                  description: formData.description,
                  language: formData.language!.value,
                  status: formData.status!.value,
                  address: project.address,
                  city: project.city,
                  country: project.country,
                  coordinates: project.coordinates,
                  company: formData.company,
                  contactPerson: formData.contactPerson,
                };
                return projectsApi
                  .updateProject(projectId, body, { ...project, ...body }, isAdmin)
                  .then(() => onClose())
                  .catch((err) => dispatch(showErrorNotification(i18n, err)));
              }
            : undefined
        }
        submitText={canEdit ? i18n.translation.common.save : undefined}
        secondaryButton={
          <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
            {canEdit ? i18n.translation.common.cancel : i18n.translation.common.close}
          </Button>
        }
      >
        {({ register, control, formState: { errors }, reset }) => {
          useEffect(() => {
            project && reset(defaultValues);
          }, [project]);

          return (
            <FormFieldsContainer fullWidth>
              <FormField label={i18n.translation.common.name} error={errors.name}>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isLoading}
                    readOnly={!canEdit}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
              <Controller
                name="language"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultValues.language}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.languages.singular} error={fieldState.error}>
                    {({ labelId }) => (
                      <CustomSelect
                        {...field}
                        inputId={labelId}
                        options={languages}
                        isDisabled={isLoading || !canEdit}
                        getOptionLabel={(x) => x.display}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultValues.status}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.status} error={fieldState.error}>
                    {({ labelId }) => (
                      <CustomSelect
                        {...field}
                        inputId={labelId}
                        options={stati}
                        isDisabled={isLoading || !canEdit}
                        getOptionLabel={(x) => x.display}
                      />
                    )}
                  </FormField>
                )}
              />
              <FormField label={i18n.translation.common.company} error={errors.company} isOptional>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isLoading}
                    readOnly={!canEdit}
                    {...register("company", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>
              <FormField label={i18n.translation.common.contactPerson} error={errors.contactPerson} isOptional>
                {({ labelId, isOptional, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isLoading}
                    readOnly={!canEdit}
                    {...register("contactPerson", {
                      required: !isOptional,
                    })}
                  />
                )}
              </FormField>
              <FormField label={i18n.translation.common.description} error={errors.description} isOptional>
                {({ labelId, isOptional, isInvalid }) => (
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isLoading || !canEdit}
                    rows={5}
                    {...register("description", { required: !isOptional })}
                  />
                )}
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ProjectInfosDialog;
